import { useMemo } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import DateField from '../../../molecules/DateField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import SelectTextField from '../../../molecules/SelectTextField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import type { ErrorField } from '../../../../types/ErrorField';
import useLegalCaseWithdrawalForm, {
    residualWithdrawalErrors,
} from '../../../../hooks/useLegalCaseWithdrawalForm';
import type { Claim, UpdateJudicialDepositPayload } from '../../../../types/LegalCase';

interface LegalCaseWithdrawalFormProps {
    errorFields: ErrorField[];
    isSaving: boolean;
    onSubmit: (value: UpdateJudicialDepositPayload) => Promise<boolean>;
    onCancel: () => void;
    // Abatimento (Task 18): só no polo ativo, e precisa dos títulos do claim para oferecer a
    // operação-alvo. Ausentes = form de levantamento simples (sem abater).
    claim?: Claim | null;
    canAbate?: boolean;
}

// Opções de operação a abater: as chaves `(tipo, referência)` únicas dos títulos não-garantia do
// claim. Garantia fica fora (ADR 002). Fora do componente (diretriz: sem função dentro de função).
const abateOptions = (claim?: Claim | null): { value: string; label: string }[] => {
    const seen = new Map<string, string>();
    for (const item of claim?.items || []) {
        if (item.isGuarantee || !item.obligationType || !item.externalReference) continue;
        const key = `${item.obligationType}|${item.externalReference}`;
        if (!seen.has(key)) {
            seen.set(key, `${item.obligationTypeFormatted} ${item.externalReference}`);
        }
    }
    return Array.from(seen, ([value, label]) => ({ value, label }));
};

// Formulário de levantamento (edita o depósito com valor + data do alvará). Valor e data andam
// juntos — a coerência é validada no backend. No polo ativo, oferece abater uma operação da
// pretensão com o valor levantado (Task 18). Só JSX + bindings.
const LegalCaseWithdrawalForm = ({
    errorFields,
    isSaving,
    onSubmit,
    onCancel,
    claim,
    canAbate = false,
}: LegalCaseWithdrawalFormProps) => {
    const options = useMemo(() => abateOptions(claim), [claim]);
    // Operação única = alvo natural, já selecionado (ADR 005, decisão 4: *"este alvará abate qual
    // operação?"* o jurídico responde; com uma só, a resposta é óbvia e pedir o clique é atrito).
    // Com várias, fica em branco de propósito — escolher a errada é pior que escolher depois.
    const form = useLegalCaseWithdrawalForm({
        onSubmit,
        initialAbateKey: options.length === 1 ? options[0].value : '',
    });
    const showAbate = canAbate && options.length > 0;

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
                Registrar levantamento (valor efetivamente transferido por alvará)
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CurrencyInput
                    label="Valor levantado"
                    prefix="R$"
                    fractionDigits={2}
                    value={form.withdrawnAmount}
                    onChange={form.setWithdrawnAmount}
                    errorMessage={getErrorMessage(errorFields, 'withdrawnAmount')}
                />
                <DateField
                    label="Data do levantamento"
                    value={form.withdrawnAt}
                    onChange={form.setWithdrawnAt}
                    errorMessage={getErrorMessage(errorFields, 'withdrawnAt')}
                />
            </div>

            {showAbate && (
                <div className="space-y-1">
                    <SelectTextField
                        label="Abater da pretensão (opcional)"
                        items={[{ value: '', label: '— não abater' }, ...options]}
                        value={form.abateKey}
                        onChange={(e) => form.setAbateKey(e.target.value)}
                        // Os dois campos do abatimento viajam juntos e vêm deste único seletor, mas
                        // o backend valida cada um. Mostrar só o erro da referência descartava em
                        // silêncio a mensagem de `abateObligationType`, deixando o usuário sem
                        // retorno num 422 legítimo.
                        errorMessage={
                            getErrorMessage(errorFields, 'abateExternalReference')
                            || getErrorMessage(errorFields, 'abateObligationType')
                            || getErrorMessage(errorFields, 'abate')
                        }
                    />
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">
                        O valor levantado reduz o saldo a receber da operação escolhida.
                    </p>
                </div>
            )}

            {residualWithdrawalErrors(errorFields).map((e) => (
                <div key={e.fieldName} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                    <FiAlertTriangle size={14} />
                    <span>{e.message}</span>
                </div>
            ))}

            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    onClick={form.submit}
                    disabled={isSaving || !form.canSubmit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : 'Registrar levantamento'}
                </button>
            </div>
        </div>
    );
};

export default LegalCaseWithdrawalForm;
